import { Attachment } from '@src/idb';
import { EditorConfig } from '@src/ui/Editor/constants/editorConfig';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import { Editor } from '@tiptap/react';
import React, { useEffect, useRef } from 'react';

export type UseTiptapFileHandlerResult = {
  handleSelectFile: (fileList: FileList) => Promise<void>;
  handleDeleteFile: (attachment: Attachment) => Promise<void>;
  attachmentError: string;
  setAttachmentError: (msg: string) => void;
  resetErrorMessage: () => void;
};

export interface UseTiptapFileHandlerProps {
  editor: Editor | null;
  editorConfig: EditorConfig;
  useAlerts: boolean;
  enableImageInsertion: boolean;
  attachments: Attachment[];
  onAttachmentChange?: (newAttachments: Attachment[]) => void;
  uploadAttachmentApi?: (file: File) => Promise<Attachment | null>;
  deleteAttachmentApi?: (attachment: Attachment) => Promise<void>;
  isEditing?: boolean;
  customUploadFunction?: (file: File) => Promise<string>;
}

/**
 * Custom hook for handling file operations in Tiptap Editor
 * Supports both Community and Tenant (Portal) contexts with appropriate validation and upload logic
 */
export function useTiptapFileHandler({
  editor,
  editorConfig,
  useAlerts,
  enableImageInsertion,
  attachments,
  onAttachmentChange,
  uploadAttachmentApi,
  deleteAttachmentApi,
  isEditing = false,
  customUploadFunction,
}: UseTiptapFileHandlerProps): UseTiptapFileHandlerResult {
  const attachmentsRef = useRef(attachments);
  const [attachmentError, setAttachmentError] = React.useState<string>('');

  const isUploading = useRef(false);

  const resetErrorMessage = React.useCallback(() => {
    setAttachmentError('');
  }, []);

  const validate = React.useCallback(
    (file: File): boolean => {
      if (file.size > editorConfig.maxSize) {
        const sizeMB = editorConfig.maxSize / (1024 * 1024);
        if (useAlerts) {
          alert(ERROR_MESSAGES.FILE.MAX_SIZE_EXCEEDED(sizeMB));
        } else {
          setAttachmentError(ERROR_MESSAGES.FILE.FILE_TOO_LARGE(sizeMB));
        }
        return false;
      }
      const extension = file.name.split('.').pop() || '';
      if (
        !editorConfig.acceptedFileTypes.includes(file.type) &&
        !editorConfig.iosFileExtensions?.includes(extension)
      ) {
        if (useAlerts) {
          alert(ERROR_MESSAGES.FILE.INVALID_FORMAT);
        } else {
          setAttachmentError(ERROR_MESSAGES.FILE.UNSUPPORTED_FORMAT);
        }
        return false;
      }
      if (!editorConfig.acceptedFileExtensions.includes(extension)) {
        if (useAlerts) {
          alert(ERROR_MESSAGES.FILE.INVALID_FORMAT);
        } else {
          setAttachmentError(ERROR_MESSAGES.FILE.UNSUPPORTED_FORMAT);
        }
        return false;
      }
      if (!useAlerts) {
        setAttachmentError('');
      }
      return true;
    },
    [editorConfig, useAlerts],
  );

  const upload = React.useCallback(
    async (file: Attachment) => {
      if (!onAttachmentChange) {
        return;
      }

      if (enableImageInsertion && editor && file.type.startsWith('image/')) {
        if (customUploadFunction) {
          try {
            const uploadedUrl = await customUploadFunction(
              new File([file.blob], file.name, { type: file.type }),
            );
            if (uploadedUrl) {
              editor.view.dom.focus({ preventScroll: true });
              editor.chain().focus().setImage({ src: uploadedUrl }).run();
              setTimeout(() => {
                const pos = editor.state.selection.$to.pos;
                editor.view.dom.focus({ preventScroll: true });
                editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
              }, 0);
            }
          } catch (error) {
            console.error('Upload failed:', error);
            setAttachmentError(ERROR_MESSAGES.FILE.UPLOAD_FAILED);
          }
          return;
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            const src = reader.result as string;
            editor.view.dom.focus({ preventScroll: true });
            editor.chain().focus().setImage({ src }).run();
            setTimeout(() => {
              const pos = editor.state.selection.$to.pos;
              editor.view.dom.focus({ preventScroll: true });
              editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
            }, 0);
          };
          reader.readAsDataURL(new File([file.blob], file.name, { type: file.type }));
          return;
        }
      }

      if (uploadAttachmentApi) {
        const uploaded = await uploadAttachmentApi(
          new File([file.blob], file.name, { type: file.type }),
        );
        if (uploaded) {
          const index = attachmentsRef.current.findIndex((item) => item === file);
          if (index !== -1) {
            const newAttachments = attachmentsRef.current.map((item, i) =>
              i === index ? uploaded : item,
            );
            attachmentsRef.current = newAttachments;
            onAttachmentChange(newAttachments);
          }
        }
      }
    },
    [onAttachmentChange, enableImageInsertion, editor, customUploadFunction, uploadAttachmentApi],
  );

  const handleSelectFile = React.useCallback(
    async (fileList: FileList) => {
      if (!onAttachmentChange) {
        return;
      }
      const total = attachments.length + fileList.length;
      if (total > editorConfig.maxFiles) {
        setAttachmentError(ERROR_MESSAGES.FILE.TOO_MANY_FILES);
        return;
      }

      for (const file of fileList) {
        if (!validate(file)) {
          return;
        }
      }

      const newAttachments: Attachment[] = [];
      Array.from(fileList).forEach((file) => {
        const provisionalAttachment: Attachment = {
          name: file.name,
          type: file.type,
          size: file.size,
          blob: file,
        };
        newAttachments.push(provisionalAttachment);
        attachmentsRef.current.push(provisionalAttachment);
      });

      onAttachmentChange([...attachmentsRef.current]);

      isUploading.current = true;

      try {
        for (const attachment of newAttachments) {
          await upload(attachment);
        }
      } finally {
        isUploading.current = false;
      }
    },
    [onAttachmentChange, attachments.length, editorConfig.maxFiles, validate, upload],
  );

  const handleDeleteFile = React.useCallback(
    async (attachmentToDelete: Attachment) => {
      if (!onAttachmentChange) {
        return;
      }
      if (deleteAttachmentApi && !isEditing) {
        await deleteAttachmentApi(attachmentToDelete);
        attachmentsRef.current = attachmentsRef.current.filter(
          (attachment) => attachment !== attachmentToDelete,
        );
      }
      onAttachmentChange(attachments.filter((attachment) => attachment !== attachmentToDelete));
    },
    [onAttachmentChange, deleteAttachmentApi, isEditing, attachments],
  );

  useEffect(() => {
    // Androidでファイル添付画面から戻ってくる際に、visibilitychangeが発火し下書きが読み込まれる。
    // attachmentsがロールバックされてしまうので、ファイル送信中はuseEffectによるattachmentsの減少は反映させない
    if (isUploading.current && attachmentsRef.current.length > attachments.length) {
      return;
    }
    attachmentsRef.current = attachments;
  }, [attachments]);

  return {
    handleSelectFile,
    handleDeleteFile,
    attachmentError,
    setAttachmentError,
    resetErrorMessage,
  };
}
